import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { TrendingUp, DollarSign, Package, Users, ShoppingCart, Calendar, Filter, BarChart3, PieChart, Activity, TrendingDown, ArrowUp, ArrowDown, Truck, Settings } from 'lucide-react';
import DailyChart from './DailyChart';
import TopItemChart from './TopItemChart';
import CategoryChart from './CategoryChart';
import SalesTrendsChart from './SalesTrendsChart';
import AdminProfileManagement from './AdminProfileManagement';
import ShippingManager from './ShippingManager';
import PickupManager from './PickupManager';
import './AdminDashboard.css';

function AdminDashboard({ user }) {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChart, setSelectedChart] = useState('daily');
  const [chartTimeFilters, setChartTimeFilters] = useState({
    daily: 'daily',
    topItem: 'daily',
    category: 'daily',
    salesTrends: 'daily'
  });
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    totalProducts: 0,
    avgOrderValue: 0,
    topItem: null,
    topCategory: null
  });
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showShipping, setShowShipping] = useState(false);
  const [showPickup, setShowPickup] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    updateStats();
  }, [orders]);

  const updateStats = () => {
    const filteredOrders = getFilteredData('daily');
    const totalRevenue = filteredOrders.reduce((sum, order) => sum + (order.total || 0), 0);
    const totalOrders = filteredOrders.length;
    const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    const topProducts = getTopProducts('daily');
    const topCategory = getTopCategory('daily');

    setStats(prev => ({
      ...prev,
      totalRevenue,
      totalOrders,
      avgOrderValue,
      topItem: topProducts[0] || null,
      topCategory: topCategory ? topCategory[0] : null
    }));
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Check if admin is authorized
      if (user) {
        const adminQuery = query(
          collection(db, 'adminProfiles'),
          where('userId', '==', user.uid)
        );
        const adminSnapshot = await getDocs(adminQuery);
        setIsAuthorized(!adminSnapshot.empty);
      }

      // Load orders
      const ordersSnapshot = await getDocs(collection(db, 'orders'));
      const ordersData = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Load products
      const productsSnapshot = await getDocs(collection(db, 'products'));
      const productsData = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setOrders(ordersData);
      setProducts(productsData);

      // Calculate base stats
      const totalProducts = productsData.length;

      setStats(prev => ({
        ...prev,
        totalProducts
      }));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredData = (filter = 'daily') => {
    const now = new Date();
    const filteredOrders = orders.filter(order => {
      if (!order.createdAt) return false;
      const orderDate = order.createdAt.toDate();
      
      switch (filter) {
        case 'daily':
          return orderDate.toDateString() === now.toDateString();
        case 'weekly':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return orderDate >= weekAgo;
        case 'monthly':
          return orderDate.getMonth() === now.getMonth() && 
                 orderDate.getFullYear() === now.getFullYear();
        case 'yearly':
          return orderDate.getFullYear() === now.getFullYear();
        default:
          return true;
      }
    });

    return filteredOrders;
  };

  const updateChartTimeFilter = (chartType, filter) => {
    setChartTimeFilters(prev => ({
      ...prev,
      [chartType]: filter
    }));
  };

  const getSalesData = (filter = 'daily') => {
    const filteredOrders = getFilteredData(filter);
    const salesData = {};
    
    filteredOrders.forEach(order => {
      if (order.createdAt) {
        let key;
        const date = order.createdAt.toDate();
        
        switch (filter) {
          case 'daily':
            key = date.toLocaleTimeString('en-US', { hour: '2-digit', hour12: false });
            break;
          case 'weekly':
            key = date.toLocaleDateString('en-US', { weekday: 'short' });
            break;
          case 'monthly':
            key = date.toLocaleDateString('en-US', { day: 'numeric' });
            break;
          case 'yearly':
            key = date.toLocaleDateString('en-US', { month: 'short' });
            break;
        }
        
        salesData[key] = (salesData[key] || 0) + (order.total || 0);
      }
    });

    return salesData;
  };

  const getCategorySales = (filter = 'daily') => {
    const filteredOrders = getFilteredData(filter);
    const categoryData = {};
    
    filteredOrders.forEach(order => {
      order.items?.forEach(item => {
        const category = item.category || 'Uncategorized';
        categoryData[category] = (categoryData[category] || 0) + (item.price * item.quantity);
      });
    });

    return categoryData;
  };

  const getTopProducts = (filter = 'daily') => {
    const filteredOrders = getFilteredData(filter);
    const productSales = {};
    
    filteredOrders.forEach(order => {
      order.items?.forEach(item => {
        productSales[item.name] = (productSales[item.name] || 0) + item.quantity;
      });
    });

    return Object.entries(productSales)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10)
      .map(([name, quantity]) => ({ name, quantity }));
  };

  const getTopCategory = (filter = 'daily') => {
    const filteredOrders = getFilteredData(filter);
    const categorySales = {};
    
    filteredOrders.forEach(order => {
      order.items?.forEach(item => {
        const category = item.category || 'Uncategorized';
        categorySales[category] = (categorySales[category] || 0) + item.quantity;
      });
    });

    const topCategory = Object.entries(categorySales)
      .sort(([,a], [,b]) => b - a)[0];

    return topCategory;
  };

  const getOrderStatusData = () => {
    const statusData = {
      pending: 0,
      processing: 0,
      shipped: 0,
      delivered: 0,
      cancelled: 0
    };

    orders.forEach(order => {
      const status = order.status || 'pending';
      if (statusData.hasOwnProperty(status)) {
        statusData[status]++;
      }
    });

    return statusData;
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (!isAuthorized) {
    return <AdminProfileManagement user={user} />;
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <div>
            <h1>Admin Dashboard</h1>
            <p>Manage your Trini Tees business</p>
          </div>
          <div className="header-buttons">
            <button 
              className="shipping-button"
              onClick={() => setShowShipping(!showShipping)}
            >
              <Truck size={20} />
              {showShipping ? 'Hide Shipping' : 'Shipping'}
            </button>
            <button 
              className="pickup-button"
              onClick={() => setShowPickup(!showPickup)}
            >
              <Package size={20} />
              {showPickup ? 'Hide Pickup' : 'Pickup'}
            </button>
            <button 
              className="profile-button"
              onClick={() => setShowProfile(!showProfile)}
            >
              <Users size={20} />
              {showProfile ? 'Hide Profile' : 'Admin Profile'}
            </button>
          </div>
        </div>
      </div>

      {showProfile && (
        <div className="profile-section">
          <AdminProfileManagement user={user} />
        </div>
      )}

      {showShipping && (
        <div className="shipping-section">
          <ShippingManager />
        </div>
      )}

      {showPickup && (
        <div className="pickup-section">
          <PickupManager />
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card revenue">
          <div className="stat-icon">
            <DollarSign size={32} />
          </div>
          <div className="stat-content">
            <h3>Total Revenue</h3>
            <p className="stat-value">${stats.totalRevenue.toFixed(2)}</p>
            <span className="stat-change positive">+12.5%</span>
          </div>
        </div>

        <div className="stat-card orders">
          <div className="stat-icon">
            <ShoppingCart size={32} />
          </div>
          <div className="stat-content">
            <h3>Total Orders</h3>
            <p className="stat-value">{stats.totalOrders}</p>
            <span className="stat-change positive">+8.2%</span>
          </div>
        </div>

        <div className="stat-card products">
          <div className="stat-icon">
            <Package size={32} />
          </div>
          <div className="stat-content">
            <h3>Products</h3>
            <p className="stat-value">{stats.totalProducts}</p>
            <span className="stat-change neutral">0%</span>
          </div>
        </div>

        <div className="stat-card avg-order">
          <div className="stat-icon">
            <TrendingUp size={32} />
          </div>
          <div className="stat-content">
            <h3>Avg Order Value</h3>
            <p className="stat-value">${stats.avgOrderValue.toFixed(2)}</p>
            <span className="stat-change positive">+5.3%</span>
          </div>
        </div>

        <div className="stat-card top-item">
          <div className="stat-icon">
            <Activity size={32} />
          </div>
          <div className="stat-content">
            <h3>Top Item</h3>
            <p className="stat-value">{stats.topItem?.name || 'N/A'}</p>
            <span className="stat-change neutral">{stats.topItem?.quantity || 0} sold</span>
          </div>
        </div>

        <div className="stat-card top-category">
          <div className="stat-icon">
            <BarChart3 size={32} />
          </div>
          <div className="stat-content">
            <h3>Top Category</h3>
            <p className="stat-value">{stats.topCategory || 'N/A'}</p>
            <span className="stat-change neutral">Most popular</span>
          </div>
        </div>
      </div>

      <div className="chart-selector">
        <h3>Analytics Charts</h3>
        <div className="chart-buttons">
          <button
            className={`chart-btn ${selectedChart === 'daily' ? 'active' : ''}`}
            onClick={() => setSelectedChart('daily')}
          >
            <Activity size={20} />
            Daily Overview
          </button>
          <button
            className={`chart-btn ${selectedChart === 'topItem' ? 'active' : ''}`}
            onClick={() => setSelectedChart('topItem')}
          >
            <TrendingUp size={20} />
            Top Items
          </button>
          <button
            className={`chart-btn ${selectedChart === 'category' ? 'active' : ''}`}
            onClick={() => setSelectedChart('category')}
          >
            <PieChart size={20} />
            Categories
          </button>
          <button
            className={`chart-btn ${selectedChart === 'salesTrends' ? 'active' : ''}`}
            onClick={() => setSelectedChart('salesTrends')}
          >
            <BarChart3 size={20} />
            Sales Trends
          </button>
        </div>
      </div>

      <div className="chart-display">
        {selectedChart === 'daily' && (
          <DailyChart 
            orders={orders}
            timeFilter={chartTimeFilters.daily}
            onTimeFilterChange={(filter) => updateChartTimeFilter('daily', filter)}
          />
        )}
        {selectedChart === 'topItem' && (
          <TopItemChart 
            orders={orders}
            timeFilter={chartTimeFilters.topItem}
            onTimeFilterChange={(filter) => updateChartTimeFilter('topItem', filter)}
          />
        )}
        {selectedChart === 'category' && (
          <CategoryChart 
            orders={orders}
            timeFilter={chartTimeFilters.category}
            onTimeFilterChange={(filter) => updateChartTimeFilter('category', filter)}
          />
        )}
        {selectedChart === 'salesTrends' && (
          <SalesTrendsChart 
            orders={orders}
            timeFilter={chartTimeFilters.salesTrends}
            onTimeFilterChange={(filter) => updateChartTimeFilter('salesTrends', filter)}
          />
        )}
      </div>
    </div>
  );
}

function getStatusColor(status) {
  const colors = {
    pending: '#f59e0b',
    processing: '#3b82f6',
    shipped: '#8b5cf6',
    delivered: '#10b981',
    cancelled: '#ef4444'
  };
  return colors[status] || '#6b7280';
}

export default AdminDashboard;
